import DirectionsIcon from '@mui/icons-material/Directions'
import { Box, Chip, CircularProgress, IconButton, List, ListItem, OutlinedInput, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from '@uidotdev/usehooks'
import ArrivalsList from 'src/components/ArrivalsList'
import { LineChip } from 'src/components/LineChip'
import { RouteFilter } from 'src/components/RouteFilter'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useGetStopStopsStopIdGet, useListScheduleStopsStopIdScheduleDateGet } from 'src/generated/brezavta/queries'
import { Arrival } from 'src/generated/brezavta/requests/types.gen'
import { useGeolocationDialog } from 'src/lib/geolocation'

import { formatTime, type FormatType } from 'src/lib/util'

export const Route = createFileRoute('/stop/$stopId')({
    component: Stop,
})

function Stop() {
    const navigate = useNavigate()
    const { stopId } = Route.useParams()
    const { t } = useTranslation()
    const theme = useTheme()

    const { map, mapRef } = useContext(MapRenderContext)
    const { acquireLocation, LoadingDialog } = useGeolocationDialog()

    const [tab, setTab] = useState('arrivals')
    const [centered, setCentered] = useState(false)
    const [selectedArrivalRoutes, setSelectedArrivalRoutes] = useState<Set<string>>(new Set())
    const [allArrivalRoutes, setAllArrivalRoutes] = useState<Array<{ shortName: string, color: string, textColor: string }>>([])
    const [selectedTimetableRoutes, setSelectedTimetableRoutes] = useState<Set<string>>(new Set())
    const [allTimetableRoutes, setAllTimetableRoutes] = useState<Array<{ shortName: string, color: string, textColor: string }>>([])
    const [timeDisplayPreference] = useLocalStorage<FormatType>('timeDisplayPreference', 'relative')

    const todayStr = DateTime.local({ zone: 'Europe/Ljubljana' }).toFormat('yyyyMMdd')
    const [scheduleDate, setScheduleDate] = useState(todayStr)

    const { data: arrivalsData, isLoading: isArrivalsLoading } = useGetStopStopsStopIdGet({ path: { stop_id: stopId }, query: { date: todayStr, current: true } })
    const { data: scheduleData, isLoading: isScheduleLoading, refetch: refetchSchedule } = useListScheduleStopsStopIdScheduleDateGet({ path: { stop_id: stopId, date: scheduleDate } })

    // Helper function to extract and sort routes from any array of items
    const buildAndSortRoutes = (items: Array<Arrival | { route_short_name?: string, trip_headsign?: string | null, route_color_background?: string, route_color_text?: string }>): { routesList: Array<{ shortName: string, color: string, textColor: string }>, routeKeys: IterableIterator<string> } => {
        const routes = new Map<string, { shortName: string, color: string, textColor: string }>()
        items.forEach((item) => {
            // Use short_name if available, otherwise use trip_headsign as the key
            const routeKey = item.route_short_name || item.trip_headsign
            if (routeKey && !routes.has(routeKey)) {
                routes.set(routeKey, {
                    shortName: routeKey,
                    color: item.route_color_background,
                    textColor: item.route_color_text,
                })
            }
        })
        // Sort routes by numeric value (extract first sequence of digits, leading or trailing)
        const routesList = Array.from(routes.values()).sort((a, b) => {
            const aMatch = a.shortName.match(/\d+/)
            const bMatch = b.shortName.match(/\d+/)
            const aNum = aMatch ? parseInt(aMatch[0]) : 9999
            const bNum = bMatch ? parseInt(bMatch[0]) : 9999
            return aNum - bNum
        })
        return { routesList, routeKeys: routes.keys() }
    }

    // Populate available routes for arrivals from arrivals, fallback to timetable when arrivals are empty
    useEffect(() => {
        const arrivals = arrivalsData?.arrivals || []
        const fallbackSchedule = scheduleData?.schedule || []
        const sourceItems = arrivals.length > 0 ? arrivals : fallbackSchedule

        if (sourceItems.length > 0) {
            const { routesList, routeKeys } = buildAndSortRoutes(sourceItems)
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setAllArrivalRoutes(routesList)

            setSelectedArrivalRoutes(new Set(routeKeys))
            return
        }

        setAllArrivalRoutes([])

        setSelectedArrivalRoutes(new Set())
    }, [arrivalsData?.arrivals, scheduleData?.schedule])

    // Populate available routes for timetable from timetable data only
    useEffect(() => {
        if (scheduleData?.schedule && scheduleData.schedule.length > 0) {
            const { routesList, routeKeys } = buildAndSortRoutes(scheduleData.schedule)
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setAllTimetableRoutes(routesList)

            setSelectedTimetableRoutes(new Set(routeKeys))
            return
        }

        setAllTimetableRoutes([])

        setSelectedTimetableRoutes(new Set())
    }, [scheduleData?.schedule])

    // Center map on stop location
    useEffect(() => {
        if (map && mapRef && arrivalsData && !centered) {
            map.current!.flyTo({
                center: [arrivalsData.lon, arrivalsData.lat],
                zoom: 15,
            })
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCentered(true)
        }
    }, [map, mapRef, arrivalsData, centered])

    function getNextTime(arrival, arrivals, index, format, t) {
    // find next arrival which has the same route id and headsign
        for (let i = index + 1; i < arrivals.length; i++) {
            if (arrival.route_id === arrivals[i].route_id && arrival.trip_headsign === arrivals[i].trip_headsign) {
                return t('Next') + ' ' + formatTime(arrivals[i].arrival_realtime, arrivals[i].departure_realtime, format, t)
            }
        }
        return t('Last trip')
    }

    function isPassed(seconds) {
        let now = DateTime.local({ zone: 'Europe/Ljubljana' })
        now = parseInt(now.toFormat('HH')) * 60 * 60 + parseInt(now.toFormat('mm')) * 60 + parseInt(now.toFormat('ss'))
        const time = seconds - now

        if (time < 0 && todayStr === scheduleDate) {
            return true
        }
        return false
    }

    if (isArrivalsLoading) {
        return (
            <AutomaticSheet>
                <Box sx={{ display: 'flex', height: window.innerHeight / 2, justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </Box>
            </AutomaticSheet>
        )
    }

    return (
        <AutomaticSheet>
            <title>{arrivalsData?.name}</title>
            <SheetHeader
                heading={arrivalsData?.name ?? stopId}
                actions={arrivalsData && (
                    <IconButton
                        size="small"
                        aria-label={t('Navigate to')}
                        onClick={() => {
                            acquireLocation(
                                location => navigate({
                                    to: '/plan',
                                    search: {
                                        from: {
                                            title: t('My location'),
                                            latitude: location.latitude,
                                            longitude: location.longitude,
                                            type: 'GPS' as const,
                                        },
                                        to: {
                                            title: arrivalsData.name,
                                            latitude: arrivalsData.lat,
                                            longitude: arrivalsData.lon,
                                            type: 'POI' as const,
                                        },
                                    } as Record<string, unknown> as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                }),
                                () => navigate({
                                    to: '/plan',
                                    search: {
                                        to: {
                                            title: arrivalsData.name,
                                            latitude: arrivalsData.lat,
                                            longitude: arrivalsData.lon,
                                            type: 'POI' as const,
                                        },
                                    } as Record<string, unknown> as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                }),
                            )
                        }}
                    >
                        <DirectionsIcon fontSize="small" />
                    </IconButton>
                )}
            />
            <LoadingDialog />
            <Stack sx={{ paddingX: 2, paddingBottom: 1 }}>
                <Box>
                    <ToggleButtonGroup
                        value={tab}
                        exclusive
                        fullWidth
                        size="small"
                        onChange={(event: React.MouseEvent<HTMLElement>) => {
                            const target = event.currentTarget as HTMLElement
                            const value = target.getAttribute('value')
                            if (value) setTab(value)
                        }}
                        sx={{ justifyContent: 'center', color: 'primary.main' }}
                    >
                        <ToggleButton value="arrivals">{t('Arrivals')}</ToggleButton>
                        <ToggleButton value="timetable">{t('Timetable')}</ToggleButton>
                    </ToggleButtonGroup>

                </Box>
                {tab === 'arrivals' && (
                    <RouteFilter
                        allRoutes={allArrivalRoutes}
                        selectedRoutes={selectedArrivalRoutes}
                        onRouteSelect={(routeShortName, isFirstChange) => {
                            if (isFirstChange) {
                                setSelectedArrivalRoutes(new Set([routeShortName]))
                            }
                            else {
                                const newSelected = new Set(selectedArrivalRoutes)
                                if (newSelected.has(routeShortName)) {
                                    newSelected.delete(routeShortName)
                                }
                                else {
                                    newSelected.add(routeShortName)
                                }

                                if (newSelected.size === 0) {
                                    setSelectedArrivalRoutes(new Set(allArrivalRoutes.map(r => r.shortName)))
                                }
                                else {
                                    setSelectedArrivalRoutes(newSelected)
                                }
                            }
                        }}
                        onResetFilter={() => {
                            setSelectedArrivalRoutes(new Set(allArrivalRoutes.map(r => r.shortName)))
                        }}
                        stopId={stopId}
                    />
                )}
                {tab === 'timetable' && (
                    <RouteFilter
                        allRoutes={allTimetableRoutes}
                        selectedRoutes={selectedTimetableRoutes}
                        onRouteSelect={(routeShortName, isFirstChange) => {
                            if (isFirstChange) {
                                setSelectedTimetableRoutes(new Set([routeShortName]))
                            }
                            else {
                                const newSelected = new Set(selectedTimetableRoutes)
                                if (newSelected.has(routeShortName)) {
                                    newSelected.delete(routeShortName)
                                }
                                else {
                                    newSelected.add(routeShortName)
                                }

                                if (newSelected.size === 0) {
                                    setSelectedTimetableRoutes(new Set(allTimetableRoutes.map(r => r.shortName)))
                                }
                                else {
                                    setSelectedTimetableRoutes(newSelected)
                                }
                            }
                        }}
                        onResetFilter={() => {
                            setSelectedTimetableRoutes(new Set(allTimetableRoutes.map(r => r.shortName)))
                        }}
                        stopId={stopId}
                    />
                )}
            </Stack>
            <div style={{ display: tab === 'arrivals' ? 'block' : 'none' }}>
                <ArrivalsList
                    arrivals={arrivalsData?.arrivals || []}
                    formatTime={formatTime}
                    getNextTime={getNextTime}
                    selectedRoutes={selectedArrivalRoutes}
                    timeFormat={timeDisplayPreference}
                />
            </div>
            <div style={{ display: tab === 'timetable' ? 'block' : 'none' }}>
                {isScheduleLoading
                    ? (
                            <Box sx={{ display: 'flex', height: window.innerHeight / 2, justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress />
                            </Box>
                        )
                    : (
                            <>
                                <Box
                                    sx={{
                                        marginX: 2,
                                    }}
                                >

                                    <OutlinedInput
                                        type="date"
                                        fullWidth
                                        size="small"
                                        value={scheduleDate.substring(0, 4) + '-' + scheduleDate.substring(4, 6) + '-' + scheduleDate.substring(6, 8)}
                                        onChange={(e) => {
                                            setScheduleDate(e.target.value.replace(/-/g, ''))
                                            refetchSchedule()
                                        }}
                                    />
                                </Box>

                                <List>
                                    {
                                        (scheduleData?.schedule || []).filter((item) => {
                                            const routeKey = item.route_short_name || item.trip_headsign
                                            return routeKey && selectedTimetableRoutes.has(routeKey)
                                        }).map((item, index) => {
                                            return (
                                                <div key={index}>
                                                    <ListItem>
                                                        <Stack direction="row" spacing={1} sx={{ width: '100%', verticalAlign: 'center' }}>
                                                            <LineChip
                                                                text={item.route_short_name}
                                                                color={item.route_color_background}
                                                                textColor={item.route_color_text}
                                                            />
                                                            <Typography component="span" variant="body2">{item.trip_headsign.toUpperCase()}</Typography>
                                                        </Stack>
                                                    </ListItem>
                                                    { /* flex so it forms a box */}
                                                    <ListItem sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                        {
                                                            item.times.map((stop_time, index) => {
                                                                return (
                                                                    <Chip
                                                                        key={index}
                                                                        variant="outlined"
                                                                        label={<span style={{ animation: stop_time.realtime && !isPassed(stop_time.departure_time) ? 'blink 2s linear infinite' : 'none' }}>{formatTime(stop_time.departure_time, stop_time.departure_time, 'absolute', t)}</span>}
                                                                        sx={{ opacity: isPassed(stop_time.departure_time) ? 0.5 : 1, color: stop_time.realtime && !isPassed(stop_time.departure_time) ? theme.palette.success.main : theme.palette.text.primary, margin: '.125rem' }}
                                                                        onClick={() => {
                                                                            navigate({
                                                                                to: `/trip/${stop_time.trip_id}/${scheduleDate}`,
                                                                            })
                                                                        }}
                                                                    />
                                                                )
                                                            })

                                                        }
                                                    </ListItem>
                                                </div>
                                            )
                                        })
                                    }
                                </List>
                            </>
                        )}
            </div>
        </AutomaticSheet>
    )
}

export default Stop
